import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import type {
  SearchRefinementInput,
  SearchSortDirection,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  AverageLimit,
  PlayerPlayingStatus,
  SearchFaceting,
  SearchOperator,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import type {
  AnalyticTag,
  Props as InstantCardSearchProps,
} from '@sorare/core/src/components/search/InstantSearch/types';
import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { FILTERS, VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { useCurrentMarketplaceUserContext } from 'contexts/currentMarketplaceUser';
import { CurrentMarketplaceUserProvider } from 'contexts/currentMarketplaceUser/Provider';
import { useDefaultPrintablePlayersSearchClient } from 'hooks/search/useDefaultPrintablePlayersSearchClient';
import { FIELDS } from 'hooks/search/usePrintablePlayersSearchClient';
import type { extractQueryGeneric } from 'lib/searchClient';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import {
  BackendPlayerSearchLayout,
  PlayerSearchLayout,
} from './PlayerSearchLayout';
import type {
  AdvancedPlayersSearchQuery,
  AdvancedPlayersSearchQueryVariables,
} from './__generated__/index.graphql';
import type { Props } from './types';

export interface FullProps extends Props {
  defaultSort: PrintablePlayerSortName;
  sorts: PrintablePlayerSortName[];
  sport: Sport;
  defaultFilters?: string[];
  getOptionalFilters?: InstantCardSearchProps['getOptionalFilters'];
  analyticsTags: AnalyticTag[];
  defaultHitsPerPage?: number;
  so5FixtureSlug?: string;
  fetchPolicy?: 'network-only' | 'cache-first';
  withStartersOn?: boolean;
}

const ADVANCED_PLAYERS_SEARCH_QUERY = gql`
  query AdvancedPlayersSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $rarity: Rarity
    $inSeason: Boolean
    $onlyPrimary: Boolean!
    $averageLimit: AverageLimit!
    $onlyFollowed: Boolean
    $so5FixtureSlug: String
    $withoutSo5Fixture: Boolean!
  ) {
    searchPlayers(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
      onlyFollowed: $onlyFollowed
    ) {
      ...useDefaultPrintablePlayersSearchClient_searchPrintablePlayers
      commonPlayerHits {
        id
        anyPlayer {
          slug
          ...BackendPlayerSearchLayout_AnyPlayerInterface
        }
      }
    }
  }
  ${useDefaultPrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ${BackendPlayerSearchLayout.fragments.AnyPlayerInterface}
` as TypedDocumentNode<
  AdvancedPlayersSearchQuery,
  AdvancedPlayersSearchQueryVariables
>;

type Query = typeof ADVANCED_PLAYERS_SEARCH_QUERY;

const FavoriteFilterChangeNotifier = ({
  onChange,
}: {
  onChange: (v: boolean) => void;
}) => {
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name,
  });

  useEffect(() => {
    if (currentRefinement !== undefined) onChange(currentRefinement);
  }, [currentRefinement, onChange]);

  return null;
};

export const AdvancedPlayerSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  so5FixtureSlug = '',
  fetchPolicy,
  withStartersOn = true,
  favoritesWatchlist = false,
  ...rest
}: FullProps) => {
  const { statsManager } = useManagePlayerSearchView();
  const index = defaultSort || rest.sorts[0];
  const [onlyFollowed, setOnlyFollowed] = useState(favoritesWatchlist);
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();
  const searchClient = useDefaultPrintablePlayersSearchClient<Query>(
    ADVANCED_PLAYERS_SEARCH_QUERY,
    {
      onlyFollowed,
      fetchPolicy,
      variables: {
        rarity: statsManager[PlayerSearchViewAttributes.RARITY],
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        averageLimit: AverageLimit.LAST_15,
        so5FixtureSlug,
        withoutSo5Fixture: !so5FixtureSlug,
      },
    },
    (data: extractQueryGeneric<Query>) => data.searchPlayers
  );

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{
        sport,
        withMyBidsBanner: false,
      }}
    >
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <InstantCardSearch
          {...(index && { indexes: [index] })}
          availableSorts={rest.sorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          getOptionalFilters={getOptionalFilters}
          urlState
          distinct
          {...(withStartersOn && {
            initialIndexUIState: {
              refinementList: {
                [FILTERS.playingStatus.attribute]: [
                  PlayerPlayingStatus.STARTER,
                  PlayerPlayingStatus.REGULAR,
                ].map(v => v.toLowerCase()),
              },
            },
          })}
          {...(defaultHitsPerPage && { defaultHitsPerPage })}
          {...(rest.attributesToRetrieve && {
            attributesToRetrieve: rest.attributesToRetrieve,
          })}
          searchClient={searchClient}
        >
          {!favoritesWatchlist && (
            <FavoriteFilterChangeNotifier onChange={setOnlyFollowed} />
          )}
          <PlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            favoritesWatchlist={favoritesWatchlist}
            {...rest}
          >
            {children}
          </PlayerSearchLayout>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};

export const BackendAdvancedPlayerSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  so5FixtureSlug = '',
  fetchPolicy,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  withStartersOn = true,
  favoritesWatchlist = false,
  ...rest
}: FullProps) => {
  const { statsManager } = useManagePlayerSearchView();
  const { locale } = useIntl();
  const index = defaultSort || rest.sorts[0];
  const [onlyFollowed /* setOnlyFollowed */] = useState(favoritesWatchlist);
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  const Wrapper = useCallback(
    ({ children: c }: { children: ReactNode }) => (
      <CurrentMarketplaceUserProvider sport={sport}>
        {c}
      </CurrentMarketplaceUserProvider>
    ),
    [sport]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  let sorts: PrintablePlayerSortName[] =
    (searchParams.get('s')?.split(',') as PrintablePlayerSortName[]) ?? [];
  if (sorts.length === 0) {
    sorts = [index];
  }

  const query = searchParams.get('q') ?? '';

  const page = Number.parseInt(searchParams.get('p') ?? '1', 10);

  const refine = useCallback(
    (attribute: string, cb: (a: string[]) => string[]) => {
      setSearchParams(prev => {
        const pv = (prev.get(attribute) ?? '')
          .split(',')
          .map(decodeURIComponent);
        const n = cb(pv);
        if (n.length) {
          prev.set(attribute, n.map(encodeURIComponent).join(','));
        } else {
          prev.delete(attribute);
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  // #region CONF
  // TODO map cardFilters
  const CONFIGURED_FILTERS = (
    [
      ['ps', 'player.playing_status', 'string'],
      ['pos', 'position', 'string'],
      ['gw', 'playing_next', 'string'],
      ['fp-is-l', 'floor_prices.in_season.limited', 'numeric'],
      ['fp-is-r', 'floor_prices.in_season.rare', 'numeric'],
      ['fp-is-sr', 'floor_prices.in_season.super_rare', 'numeric'],
      ['fp-is-u', 'floor_prices.in_season.unique', 'numeric'],
      ['fp-as-l', 'floor_prices.all_season.limited', 'numeric'],
      ['fp-as-r', 'floor_prices.all_season.rare', 'numeric'],
      ['fp-as-sr', 'floor_prices.all_season.super_rare', 'numeric'],
      ['fp-as-u', 'floor_prices.all_season.unique', 'numeric'],
    ] as [string, string | ((locale: string) => string), 'string' | 'numeric'][]
  ).map(([k, v, t]) => [k, typeof v === 'string' ? v : v(locale), t]);

  const urlToApi = Object.fromEntries(CONFIGURED_FILTERS);
  const urlToType = Object.fromEntries(
    CONFIGURED_FILTERS.map(e => [e[0], e[2]])
  );

  const apiToUrl = Object.fromEntries(
    CONFIGURED_FILTERS.map(e => e.slice(0, 2).reverse())
  );

  const activeFacets = Object.fromEntries(
    CONFIGURED_FILTERS.map(e => [
      e[0],
      searchParams
        .getAll(e[0])
        .flatMap(e2 =>
          urlToType[e[0]] === 'numeric'
            ? e2.split(encodeURIComponent(':'))
            : e2.split(',')
        ),
    ])
  );
  // #endregion

  // #region QUERY
  const { data, loading, previousData, refetch } = useQuery(
    ADVANCED_PLAYERS_SEARCH_QUERY,
    {
      variables: {
        query,
        page,
        pageSize: 20,
        advancedFilters: [
          `sport:${sport.toLowerCase()}`,
          ...(defaultFilters ?? []),
        ].join(' AND '),
        sorts: sorts.map(s => {
          const [direction, alias] = s.split('-');
          const field = FIELDS[alias as any] as string;
          return { field, direction: direction as SearchSortDirection };
        }),
        refinements: Object.entries(
          activeFacets
        ).flatMap<SearchRefinementInput>(([k, v]) => {
          if (!v.length) return [];
          if (urlToType[k] === 'numeric') {
            return v
              .map(
                (value, i) =>
                  value && {
                    field: urlToApi[k],
                    operator: i
                      ? SearchOperator.LESS_THAN_OR_EQUAL
                      : SearchOperator.GREATER_THAN_OR_EQUAL,
                    values: [{ integerValue: Number.parseInt(value, 10) }],
                  }
              )
              .filter(Boolean);
          }
          return {
            field: urlToApi[k],
            operator: SearchOperator.EQUAL,
            values: v.map(vv => ({ stringValue: vv })),
          };
        }),
        facets: CONFIGURED_FILTERS.map(e => {
          return {
            field: e[1],
            limit: 20,
            type: SearchFaceting.DISJUNCTIVE,
          };
        }),
        rarity: statsManager[PlayerSearchViewAttributes.RARITY],
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        onlyFollowed,
        averageLimit: AverageLimit.LAST_15,
        so5FixtureSlug,
        withoutSo5Fixture: !so5FixtureSlug,
      },
    }
  );
  // #endregion

  const safeData = data ?? previousData;

  return (
    <ConditionalWrapper wrap={!currentMarketplaceUserContext} Wrapper={Wrapper}>
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <GQLSearchContext.Provider
          value={{
            query,
            refineQuery: newQuery => refine('q', () => [newQuery]),
            refinements: [],
            refine,
            // TODO read from qs for faster return
            page,
            pages: safeData?.searchPlayers.nbPages ?? 0,
            advancedFilters: '', // TODO ADVANCED_FILTERS,
            onSaleOnly: true,
            activeFacets,
            sorts,
            facets: Object.fromEntries(
              safeData?.searchPlayers.facets?.map(({ field, ...rest2 }) => [
                apiToUrl[field] ?? field,
                rest2,
              ]) ?? []
            ),
          }}
        >
          {/* {!favoritesWatchlist && (
            <FavoriteFilterChangeNotifier onChange={setOnlyFollowed} />
          )} */}
          <BackendPlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            favoritesWatchlist={favoritesWatchlist}
            items={safeData?.searchPlayers.commonPlayerHits ?? []}
            loading={loading}
            refetch={refetch}
            {...rest}
          >
            {children}
          </BackendPlayerSearchLayout>
        </GQLSearchContext.Provider>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
